import {createSlice, isAnyOf, PayloadAction} from '@reduxjs/toolkit';
import {UserState} from '../../types/stateTypes';
import type {RootState} from '../store';
import {Profile, RolesObject} from '../../types/accountTypes';
import {cookieToObject} from '../../helpers/auth';
import {accountEndpoints} from '../api/accountApi';
import {clearLocalTokens} from "../../helpers/auth";

const initialState: UserState = {
    user: null,
    profiles: [],
    currentProfileID: undefined,
    membership: null,
    roles: null,
    person: null,
    isAuthenticated: false,
    staff: null,
};

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setActiveMember: (state, {payload}: PayloadAction<Profile>) => {
            // Sets profile data in state and localStorage. Eventually LS logic will be removed.
            setActiveProfile(payload, state);

            // For member accounts: new profile = new roles.  Set roles with profile.roles.
            state.roles = createRolesObject(payload.roles ?? []);

            document.cookie = `profile_hash=${payload.hash};path=/;`;
            document.cookie = `id=${payload.id};path=/;`;

            state.isAuthenticated = cookiesAuthenticated();
        },
        setLogout: () => {
            performClientLogoutTasks();
            document.cookie = `is_authenticated=0`
            // Resets state back to initialState.
            return initialState;
        },
        clearActiveMemberStaff: (state) => {
            return {
                ...initialState,
                isAuthenticated: true,
                roles: state.roles,
                staff: state.staff,
            };
        }
    },
    extraReducers: (builder) => {
        builder.addMatcher(accountEndpoints.getProfilesAndRoles.matchFulfilled, (state, {payload}) => {
            let profile = payload[0];
            let rolesObj = createRolesObject(profile.roles ?? []);
            // Set roles first, as they may change in SetMember else-block below.
            state.roles = rolesObj;

            // Set staff if incoming roles have staff roles.
            const isStaff = rolesObj['staff'] && rolesObj['staff'].length > 0;

            if (isStaff) {
                // SetStaff
                const {user, person} = profile;

                let leanProfile = {...profile};
                // Do a bit of cleanup with the profile, remove all the extra stuff from it.
                delete leanProfile.user;
                delete leanProfile.membership;
                delete leanProfile.account;
                delete leanProfile.person;

                // Updated structure of staffProfile in UserSlice to hold profile, user and person.
                state.staff = {profile: leanProfile, user, person};
            } else {
                // SetMember
                const cookies = cookieToObject();
                // If refreshed/reauthenticating the profiles, check cookies to see if there's already a profile id assigned.
                if (cookies['id']) {
                    for (let userProfile of payload) {
                        if (userProfile.id === Number(cookies['id'])) {
                            profile = userProfile;

                            // If different profile than current, update the roles with new profile.
                            rolesObj = createRolesObject(profile.roles ?? []);
                            state.roles = rolesObj;
                            break;
                        }
                    }
                }

                // Sets all available profiles on member login.
                // state.profiles will be set when staff choose a member in getProfile matcher.
                state.profiles = payload;
                setActiveProfile(profile, state);

                // Sets the currentProfileID to cookie only for member, as this will be needed for refresh.
                // Staff need the currentProfileID of the member they will represent, which happens in getProfile matcher.
                document.cookie = `id=${profile.id};path=/;`;
            }

            // Both staff and member need their hash saved to a cookie.
            document.cookie = `profile_hash=${profile.hash};path=/;`;

            state.isAuthenticated = cookiesAuthenticated();
        })
        builder.addMatcher(isAnyOf(accountEndpoints.getProfile.matchFulfilled, accountEndpoints.getSelectMember.matchFulfilled), (state, {payload}) => {
            // This matcher only runs when signed in as staff.
            const profile = payload;
            const {user, membership, person} = profile;

            // allProfiles will be updated to an array of the single member profile staff choose to represent.
            state.profiles = [payload];
            state.currentProfileID = profile.id;
            state.user = user;
            state.person = person;
            state.membership = membership ?? null;

            // ProfileChange.tsx (member) uses the setActiveMember dispatch/action, their hash updates there.
            // Staff keep initial hash. To retain member profile for staff, but update the id in the cookie.
            document.cookie = `id=${profile.id};path=/;`;

            // Staff do not update their roles when choosing a member profile to represent.
            state.isAuthenticated = cookiesAuthenticated();
        });
        builder.addMatcher(accountEndpoints.updateProfileRoles.matchFulfilled, (state, {meta}) => {
            const {profileID, roles} = meta.arg.originalArgs;

            if (profileID !== state.currentProfileID) return;

            // Update the roles in the state.
            state.roles = createRolesObject(roles);
        });
    },
});

function setActiveProfile(profile: Profile, state: UserState) {
    state.currentProfileID = profile.id;
    state.user = profile.user;
    state.person = profile.person;
    state.membership = profile.membership ?? null;
}

function createRolesObject(roles: string[]) {
    let rolesObj: RolesObject = {};
    // Cycle through all roles and organize them by domain: role.
    for (let i = 0; i < roles.length; i++) {
        let fullRole: any[] = roles[i].split('.');
        let domain: string = fullRole[0];
        let role: any = fullRole[1];

        // If the current domain doesn't exist, add it first.
        if (!rolesObj[domain as keyof typeof rolesObj]) {
            rolesObj[domain as keyof typeof rolesObj] = [];
        }
        // Add the role to the domain.
        rolesObj[domain as keyof RolesObject].push(role);
    }
    return rolesObj;
}

function cookiesAuthenticated() {
    const cookies = cookieToObject();
    return !!(cookies.hasOwnProperty('is_authenticated') && cookies.is_authenticated);
}

function selectCurrentProfile(state: RootState) {
    return state.user.profiles.find(profile => profile.id === state.user.currentProfileID);
}

function selectIsStaff(state: RootState) {
    return state.user.staff !== null;
}

function selectIsMemberAuthenticated(state: RootState) {
    // Auth gets set in getProfilesAndRoles & getProfile action. See original auth logic in auth.js isAuthenticated().
    return state.user.isAuthenticated;
}

function selectActiveMembershipId(state: RootState) {
    return state.user.membership?.id;
}

function selectAccountMembershipNumber(state: RootState) {
    const account = selectCurrentProfile(state)?.account;
    const membership = state.user.membership;
    return (!account || !membership) ? null : `${account.account_number}-${membership.membership_number}`;
}

function selectActiveAccount(state: RootState) {
    const account = selectCurrentProfile(state)?.account;
    return (!account) ? null : account;
}

function selectCurrentInitials(state: RootState) {
    const person = state.user.person;
    return person ? person.first_name.charAt(0) + person.last_name.charAt(0) : 'U';
}

function selectFullName(state: RootState) {
    const person = state.user.person;
    return person ? `${person.first_name} ${person.last_name}` : '';
}

// Checks if the current user has at least one of the specified member roles.
function selectHasMemberRole(state: RootState, roles: string[]) {
    const currentUserRoles = selectCurrentProfile(state)?.roles ?? [];
    for (let role of roles) {
        if (currentUserRoles.includes(role)) return true;
    }
    return false;
}

// Checks if the current user has at least one of the specified staff roles.
function selectHasStaffRole(state: RootState, roles: string[]) {
    const staffRoles = state.user.roles?.staff;
    if (!staffRoles) return false;
    for (let role of roles) {
        if (staffRoles.includes(role)) return true;
    }
    return false;
}

function performClientLogoutTasks() {
    clearLocalTokens();
}

const {reducer: userReducer} = userSlice;

export const {
    setActiveMember,
    setLogout,
    clearActiveMemberStaff
} = userSlice.actions;

export {
    userReducer,
    selectCurrentProfile,
    selectIsStaff,
    selectIsMemberAuthenticated,
    selectHasMemberRole,
    selectHasStaffRole,
    selectCurrentInitials,
    selectFullName,
    selectAccountMembershipNumber,
    selectActiveAccount,
    selectActiveMembershipId,
    cookiesAuthenticated,
}
